import {
  Alert,
  Button,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  InputLabel,
  Stack,
  Typography,
} from '@mui/material';
import { Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import InputField from '../elements/InputField';
import { handleWhoPurchased } from '../../api/products';

const validationSchema = yup.object({
  purchasedByName: yup.string().required('Name is required'),
  purchasedByEmail: yup
    .string()
    .email('Invalid email')
    .required('Email is required'),
  store: yup.string(),
  orderId: yup.string(),
});

const InputStyle = {
  borderRadius: '54px',
  background: 'var(--White, #FFF)',
  fontSize: { xs: '14px', sm: '16px' },
  fontWeight: '400',
  '&& .css-1t8l2tu-MuiInputBase-input-MuiOutlinedInput-input': {
    padding: '10px 16px',
  },
  '&& .css-1d3z3hw-MuiOutlinedInput-notchedOutline': {
    border: '1px solid var(--Black, #000)',
  },
};

const InputLabelStyle = {
  textAlign: 'left',
  fontSize: { xs: '14px', sm: '16px' },
  fontWeight: 700,
  mb: '8px',
  color: 'var(--Black, #000)',
  fontStyle: 'normal',
  lineHeight: '24px',
};


const PlusButton = ({ onClick, isDisabled }) => {
  return (
    <svg
      onClick={onClick}
      xmlns="http://www.w3.org/2000/svg"
      width="44"
      height="44"
      viewBox="0 0 44 44"
      fill="none"
      style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
    >
      <circle cx="22" cy="22" r="21.5" stroke={isDisabled ? 'grey' : 'black'} />
      <rect x="21.5" y="14" width="1" height="16" fill={isDisabled ? 'grey' : 'black'} />
      <rect
        x="14"
        y="22"
        width="0.999999"
        height="16"
        transform="rotate(-90 14 22)"
        fill={isDisabled ? 'grey' : 'black'}
      />
      <rect x="14" y="21.5" width="16" height="1" fill={isDisabled ? 'grey' : 'black'} />
    </svg>
  );
};


const MinusButton = ({ onClick, isDisabled }) => {
  return (
    <svg
      onClick={onClick}
      xmlns="http://www.w3.org/2000/svg"
      width="44"
      height="44"
      viewBox="0 0 44 44"
      fill="none"
      style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
    >
      <circle cx="22" cy="22" r="21.5" stroke={isDisabled ? 'grey' : 'black'} />
      <rect
        x="14"
        y="22"
        width="0.999999"
        height="16"
        transform="rotate(-90 14 22)"
        fill={isDisabled ? 'grey' : 'black'}
      />
    </svg>
  );
};


const WhoPurchasedForm = ({ onSubmit, onCancel, product }) => {
  const [quantity, setQuantity] = useState(1);
  const [quantityNeeded, setQuantityNeeded] = useState(
    product?.quantity_required
  );

  const handleIncrement = () => {
    if (quantity < quantityNeeded + 5) {
      setQuantity((prevQuantity) => prevQuantity + 1);
    }
  };

  const handleDecrement = () => {
    if (quantity > 1) {
      setQuantity((prevQuantity) => prevQuantity - 1);
    }
  };

  useEffect(() => {
    const needed = product?.quantity_purchased && product?.quantity_purchased
      ? parseInt(product?.quantity_required) -
      parseInt(product?.quantity_purchased)
      : parseInt(product?.quantity_required);
    setQuantityNeeded(needed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);
  return (
    <Formik
      initialValues={{
        purchasedByName: '',
        purchasedByEmail: '',
        store: '',
        orderId: '',
        quantityPurchased: '',
      }}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        const formData = {
          ...values,
          quantityPurchased: quantity,
        };
        onSubmit(formData);
      }}>
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        touched,
        values,
      }) => (
        <form noValidate onSubmit={handleSubmit}>
          <Grid container>
            <Grid item xs={12} sx={{ mb: '16px' }}>
              <InputLabel sx={InputLabelStyle}>Name</InputLabel>
              <InputField
                fieldType="registry"
                id="purchasedByName"
                type="purchasedByName"
                value={values.purchasedByName}
                name="purchasedByName"
                fullWidth
                error={Boolean(
                  touched.purchasedByName && errors.purchasedByName
                )}
                onBlur={handleBlur}
                onChange={handleChange}
                sx={InputStyle}
                required
              />
            </Grid>
            <Grid item xs={12} sx={{ mb: '16px' }}>
              <InputLabel sx={InputLabelStyle}>Email</InputLabel>
              <InputField
                fieldType="registry"
                id="purchasedByEmail"
                type="purchasedByEmail"
                value={values.purchasedByEmail}
                name="purchasedByEmail"
                fullWidth
                error={Boolean(
                  touched.purchasedByEmail && errors.purchasedByEmail
                )}
                onBlur={handleBlur}
                onChange={handleChange}
                sx={InputStyle}
                required
              />
            </Grid>
            <Grid item xs={12} sx={{ mb: '16px' }}>
              <Stack direction="row" justifyContent="space-between">
                <InputLabel sx={InputLabelStyle}>Quantity purchased</InputLabel>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ fontSize: { xs: '11px', sm: '13px' } }}>
                  Quantity needed: {quantityNeeded}
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center">
                <MinusButton onClick={handleDecrement} isDisabled={quantity === 1} />
                <Typography
                  variant="body2"
                  color="textPrimary"
                  sx={{ mx: '40px' }}>
                  {quantity}
                </Typography>
                <PlusButton onClick={handleIncrement} isDisabled={quantity === quantityNeeded + 5} />
              </Stack>
            </Grid>
            <Grid item xs={12} sx={{ mb: '16px' }}>
              <Stack direction="row" justifyContent="space-between">
                <InputLabel sx={InputLabelStyle}>Store</InputLabel>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ fontSize: { xs: '11px', sm: '13px' } }}>
                  Optional
                </Typography>
              </Stack>
              <InputField
                fieldType="registry"
                id="store"
                type="store"
                value={values.store}
                name="store"
                fullWidth
                placeholder="Ex. Amazon"
                error={Boolean(touched.store && errors.store)}
                onBlur={handleBlur}
                onChange={handleChange}
                sx={InputStyle}
              />
            </Grid>
            <Grid item xs={12} sx={{ mb: '16px' }}>
              <Stack direction="row" justifyContent="space-between">
                <InputLabel sx={InputLabelStyle}>Order number</InputLabel>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ fontSize: { xs: '11px', sm: '13px' } }}>
                  Optional
                </Typography>
              </Stack>
              <InputField
                fieldType="registry"
                id="orderId"
                type="orderId"
                value={values.orderId}
                name="orderId"
                fullWidth
                placeholder="Ex. 123-1234568"
                error={Boolean(touched.orderId && errors.orderId)}
                onBlur={handleBlur}
                onChange={handleChange}
                sx={InputStyle}
              />
            </Grid>
            <Grid
              container
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}>
              <Button
                type="submit"
                fullWidth
                sx={{
                  borderRadius: '54px',
                  border: '1px solid var(--Black, #000)',
                  background: 'var(--Highlighter, #D6FE51)',
                  '&:hover': {
                    background: 'var(--Highlighter, #D6FE51)',
                  },
                  mt: { sm: '20px' },
                  textTransform: 'none',
                }}>
                <Typography
                  sx={{
                    fontSize: ' 16px',
                    fontWeight: '700',
                    color: 'var(--Black, #000)',
                  }}>
                  Save
                </Typography>
              </Button>
              <Button
                type="button"
                fullWidth
                onClick={onCancel}
                sx={{
                  borderRadius: '54px',
                  border: '1px solid var(--Black, #000)',
                  background: '#FFF',
                  textTransform: 'none',
                  mt: '16px',
                }}>
                <Typography
                  sx={{
                    fontSize: ' 16px',
                    fontWeight: '700',
                    color: 'var(--Black, #000)',
                  }}>
                  Cancel
                </Typography>{' '}
              </Button>
            </Grid>
          </Grid>
        </form>
      )}
    </Formik>
  );
};

export default function WhoPurchased({
  open,
  onClose,
  product,
  registryId,
  fetchRegistryData,
  selectedProduct,
  setSelectedProduct
}) {
  const [error, setError] = useState(null);
  const [isSaved, setIsSaved] = useState(false);

  const handleCloseModal = () => {
    setSelectedProduct(null)
    onClose(false);
  };

  const handleFormSubmitted = () => {
    fetchRegistryData();
    setSelectedProduct(null)
    onClose(false);
  };

  const handleFormSubmit = async (values) => {
    try {
      const response = await handleWhoPurchased(
        values,
        product?.product_details?.id,
        registryId
      );
      if (!response.ok) {
        const errorResponse = await response.json();
        throw new Error(errorResponse.error?.message || errorResponse.message);
      }
      setIsSaved(true);
    } catch (error) {
      setError(
        error.message || 'An error occurred while updating product data.'
      );
    }
  };

  if (!open || !product) return null;

  return (
    <Dialog open={open} onClose={handleCloseModal} fullWidth maxWidth="sm" sx={{
      backdropFilter: 'blur(4px)',
      background: 'rgba(239, 234, 228, 0.72)',
      '& .MuiPaper-root': {
        borderRadius: '16px',
        border: '1px solid var(--Black, #000)',
        background: 'var(--White, #FFF)',
      },
    }}>
      {error && (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      )}
      <Container
        sx={{ padding: { xs: '16px', md: '24px' }, textAlign: 'center' }}>
        {product && product.product_details && isSaved ? (
          <>
            <Typography
              variant="body2"
              color="textPrimary"
              sx={{ textAlign: 'center', fontSize: '32px', fontWeight: '300' }}>
              All set!
            </Typography>
            <Typography
              variant="body2"
              color="textPrimary"
              sx={{ textAlign: 'center', fontSize: '16px', fontWeight: '400' }}>
              Your purchase has been saved.
            </Typography>
            <Button
              type="button"
              fullWidth
              onClick={handleFormSubmitted}
              sx={{
                borderRadius: '54px',
                border: '1px solid var(--Black, #000)',
                background: 'var(--Highlighter, #D6FE51)',
                '&:hover': {
                  background: 'var(--Highlighter, #D6FE51)',
                },
                marginTop: { xs: '16px', sm: '20px' },
                textTransform: 'none',
              }}>
              <Typography
                sx={{
                  fontSize: ' 16px',
                  fontWeight: '700',
                  color: 'var(--Black, #000)',
                }}>
                Close
              </Typography>{' '}
            </Button>
          </>
        ) : (
          <>
            {product?.product_details?.pictures.length > 0 ? (
              <img
                src={
                  selectedProduct?.product_details?.pictures[0] ||
                  '/default_product_category.png'
                }
                alt="Product"
                style={{
                  width: '128px',
                  height: '128px',
                  marginBottom: '8px',
                  borderRadius: '4px',
                  border: '1px solid var(--Black, #000)',
                }}
                onError={(e) => {
                  e.target.src = '/default_product_category.png';
                }}
              />
            ) : (
              <img
                src="/default_product_category.png"
                loading="lazy"
                style={{
                  width: '128px',
                  height: '128px',
                  marginBottom: '8px',
                  borderRadius: '4px',
                  border: '1px solid var(--Black, #000)',
                }}
                alt="Default Product"
              />
            )}

            <DialogTitle
              sx={{
                textAlign: 'center',
                fontSize: '30px',
                fontWeight: '400',
                p: 0,
              }}>{`Who is this from?`}</DialogTitle>
            <Typography
              sx={{
                textAlign: 'center',
                fontSize: '16px',
                fontWeight: '400',
                color: 'var(--Black, #000)',
                mb: '8px',
              }}>{`Let them know who to thank!`}</Typography>
            <DialogContent
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                p: 0,
              }}>
              <WhoPurchasedForm
                onSubmit={handleFormSubmit}
                onCancel={handleCloseModal}
                product={product}
              />
            </DialogContent>
          </>
        )}
      </Container>
    </Dialog>
  );
}
